import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  FiHome, FiBook, FiVideo, FiMessageSquare, FiUser, FiLogOut,
  FiChevronDown, FiMail, FiShoppingCart, FiStar, FiMenu, FiX,
  FiBookOpen, FiCalendar, FiMonitor, FiGrid, FiAward
} from 'react-icons/fi';
import CartDisplay from './CartDisplay';
import Chatbot from './Chatbot';
import { cartService } from '../services/cartService';

type PageType = 'home' | 'books' | 'videoLearning' | 'blogs' | 'faq' | 'feedback' | 'login' | 'admin' | 'onlineClasses' | 'inPersonClasses';

interface HeaderProps {
  onHomeClick: () => void;
  onBooksClick: () => void;
  onVideoLearningClick: () => void;
  onBlogsClick: () => void;
  onLoginClick: () => void;
  onAdminClick: () => void;
  onMkAcademyOnlineClick?: () => void;
  onMkAcademyInPersonClick?: () => void;
  onDashboardClick?: () => void;
  isAuthenticated?: boolean;
  isAdmin?: boolean;
  user?: any;
  onLogout?: () => void;
  currentPage?: PageType;
}

interface NavItem {
  name: string;
  href: string;
  external?: boolean;
  dropdown?: Array<{
    name: string;
    href: string;
    action?: () => void;
  }>;
}

const CSS_VARS = {
  primary: '#253C8C',
  primaryLight: '#3B4F9E',
  secondary: '#FF6B35',
  accent: '#4ECDC4',
  accentLight: '#6FDDD5',
  text: '#2C3E50',
  textLight: '#7F8C8D',
  white: '#FFFFFF',
  lightGray: '#F8F9FA',
  borderColor: '#E9ECEF',
  gradientPrimary: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  gradientSecondary: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  gradientActive: 'linear-gradient(135deg, #253C8C 0%, #3B4F9E 100%)',
  shadow: '0 2px 10px rgba(0,0,0,0.1)',
  shadowHover: '0 4px 20px rgba(37, 60, 140, 0.15)',
  shadowActive: '0 2px 8px rgba(37, 60, 140, 0.2)',
  borderRadius: '8px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  transitionFast: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
};

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: ${CSS_VARS.white};
  box-shadow: ${CSS_VARS.shadow};
`;

const TopBar = styled.div`
  background: ${CSS_VARS.primary};
  color: ${CSS_VARS.white};
  padding: 8px 0;
`;

const TopBarContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TopContactInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  
  .contact-label {
    font-size: 0.9rem;
    font-weight: 500;
  }
  
  .email {
    color: ${CSS_VARS.white};
    text-decoration: none;
    font-weight: 600;
    transition: ${CSS_VARS.transition};
    
    &:hover {
      color: ${CSS_VARS.accent};
    }
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  font-size: 0.9rem;
`;

const UserName = styled.span`
  font-weight: 600;
  
  &:hover {
    text-decoration: underline;
  }
`;

const ProfileLink = styled.button`
  background: transparent;
  border: 1px solid ${CSS_VARS.white};
  color: ${CSS_VARS.white};
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 0.8rem;
  cursor: pointer;
  margin-left: 8px;
  transition: ${CSS_VARS.transition};
  
  &:hover {
    background: ${CSS_VARS.white};
    color: ${CSS_VARS.primary};
  }
`;

const LogoutButton = styled.button`
  background: transparent;
  border: 1px solid ${CSS_VARS.white};
  color: ${CSS_VARS.white};
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 0.8rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: ${CSS_VARS.transition};
  
  &:hover {
    background: ${CSS_VARS.white};
    color: ${CSS_VARS.primary};
  }
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 80px;
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const LogoImage = styled.img`
  height: 50px;
  width: auto;
`;

const NavMenu = styled.nav`
  display: flex;
  align-items: center;
  gap: 30px;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavItem = styled.div<{ $isActive?: boolean }>`
  position: relative;
  
  .nav-link {
    display: flex;
    align-items: center;
    gap: 8px;
    color: ${props => props.$isActive ? CSS_VARS.primary : CSS_VARS.text};
    font-weight: ${props => props.$isActive ? '600' : '500'};
    font-size: 1rem;
    cursor: pointer;
    padding: 12px 16px;
    border-radius: ${CSS_VARS.borderRadius};
    transition: ${CSS_VARS.transition};
    position: relative;
    overflow: hidden;
    
    /* Active state background */
    ${props => props.$isActive && `
      background: linear-gradient(135deg, rgba(37, 60, 140, 0.08) 0%, rgba(59, 79, 158, 0.12) 100%);
      box-shadow: ${CSS_VARS.shadowActive};
    `}
    
    /* Hover effects */
    &:hover {
      color: ${CSS_VARS.primary};
      background: ${props => props.$isActive 
        ? 'linear-gradient(135deg, rgba(37, 60, 140, 0.12) 0%, rgba(59, 79, 158, 0.16) 100%)'
        : CSS_VARS.lightGray
      };
      transform: translateY(-1px);
      box-shadow: ${CSS_VARS.shadowHover};
    }
    
    /* Active indicator line */
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 50%;
      width: ${props => props.$isActive ? '80%' : '0%'};
      height: 3px;
      background: ${CSS_VARS.gradientActive};
      border-radius: 2px 2px 0 0;
      transform: translateX(-50%);
      transition: ${CSS_VARS.transition};
    }
    
    /* Hover indicator line */
    &:hover::after {
      width: 80%;
    }
    
    /* Icon animation */
    svg {
      transition: ${CSS_VARS.transitionFast};
    }
    
    &:hover svg {
      transform: scale(1.1);
      color: ${CSS_VARS.primary};
    }
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background: ${CSS_VARS.white};
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  border-radius: ${CSS_VARS.borderRadius};
  border: 1px solid ${CSS_VARS.borderColor};
  min-width: 220px;
  z-index: 1000;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-10px) scale(0.95);
  transition: ${CSS_VARS.transition};
  backdrop-filter: blur(10px);
  
  ${NavItem}:hover & {
    opacity: 1;
    visibility: visible;
    transform: translateY(0) scale(1);
  }
  
  /* Add subtle animation delay for smoother experience */
  transition-delay: 0.1s;
  
  ${NavItem}:hover & {
    transition-delay: 0s;
  }
`;

const DropdownItem = styled.div`
  padding: 0;
  
  a, div {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 20px;
    color: ${CSS_VARS.text};
    text-decoration: none;
    font-weight: 400;
    transition: ${CSS_VARS.transitionFast};
    border-radius: 6px;
    margin: 4px 8px;
    
    &:hover {
      color: ${CSS_VARS.primary};
      background: linear-gradient(135deg, rgba(37, 60, 140, 0.06) 0%, rgba(59, 79, 158, 0.08) 100%);
      transform: translateX(4px);
    }
    
    svg {
      transition: ${CSS_VARS.transitionFast};
      opacity: 0.7;
    }
    
    &:hover svg {
      opacity: 1;
      transform: scale(1.1);
      color: ${CSS_VARS.primary};
    }
  }
  
  &:first-child a, &:first-child div {
    margin-top: 8px;
  }
  
  &:last-child a, &:last-child div {
    margin-bottom: 8px;
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const AuthButton = styled.button`
  background: linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%);
  color: white;
  border: none;
  padding: 0.5rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(30, 58, 138, 0.3);
  }
`;

const IconButton = styled.button`
  background: none;
  border: none;
  padding: 0.5rem;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #6b7280;
  
  &:hover {
    background: #f3f4f6;
    color: #1e3a8a;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  color: #6b7280;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileMenu = styled.div`
  display: none;
  background: white;
  border-top: 1px solid #e5e7eb;
  padding: 1rem 0;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileNavItem = styled.div<{ $isActive?: boolean; $isSubItem?: boolean }>`
  padding: ${props => props.$isSubItem ? '0.75rem 2rem' : '0.75rem 1.5rem'};
  border-bottom: 1px solid #f3f4f6;
  cursor: pointer;
  transition: ${CSS_VARS.transitionFast};
  position: relative;
  
  /* Active state for mobile */
  ${props => props.$isActive && `
    background: linear-gradient(135deg, rgba(37, 60, 140, 0.08) 0%, rgba(59, 79, 158, 0.12) 100%);
    border-left: 4px solid ${CSS_VARS.primary};
    font-weight: 600;
    color: ${CSS_VARS.primary};
  `}
  
  /* Sub-item styling */
  ${props => props.$isSubItem && `
    background-color: #f8f9fa;
    font-size: 0.9rem;
  `}
  
  &:hover {
    background-color: ${props => props.$isActive 
      ? 'linear-gradient(135deg, rgba(37, 60, 140, 0.12) 0%, rgba(59, 79, 158, 0.16) 100%)'
      : '#f3f4f6'
    };
    transform: translateX(4px);
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  /* Icon styling in mobile */
  svg {
    transition: ${CSS_VARS.transitionFast};
  }
  
  &:hover svg {
    transform: scale(1.1);
    color: ${CSS_VARS.primary};
  }
`;

const CartBadge = styled.span`
  position: absolute;
  top: -5px;
  right: -5px;
  background: #ef4444;
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
`;

const ContactInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #6b7280;
`;

const Header: React.FC<HeaderProps> = ({ 
  onHomeClick, 
  onBooksClick, 
  onVideoLearningClick, 
  onBlogsClick,
  onLoginClick, 
  onAdminClick,
  onMkAcademyOnlineClick,
  onMkAcademyInPersonClick,
  onDashboardClick,
  isAuthenticated = false,
  isAdmin = false,
  user,
  onLogout,
  currentPage = 'home'
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  const [isCartOpen, setIsCartOpen] = useState<boolean>(false);
  const [isChatbotOpen, setIsChatbotOpen] = useState<boolean>(false);
  const [cartCount, setCartCount] = useState<number>(0);

  console.log('Header render - isCartOpen:', isCartOpen, 'cartCount:', cartCount);

  // Load cart count on component mount
  useEffect(() => {
    const loadCartCount = () => {
      const count = cartService.getCartCount();
      setCartCount(count);
    };

    loadCartCount();

    // Listen for cart updates
    const handleCartUpdate = () => {
      loadCartCount();
    };

    // Listen for openCart event
    const handleOpenCart = () => {
      setIsCartOpen(true);
    };

    window.addEventListener('cartUpdated', handleCartUpdate);
    window.addEventListener('openCart', handleOpenCart);
    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
      window.removeEventListener('openCart', handleOpenCart);
    };
  }, []);

  // Helper function to determine if a nav item is active
  const isNavItemActive = (itemName: string): boolean => {
    switch (itemName) {
      case 'Home':
        return currentPage === 'home';
      case 'Books':
        return currentPage === 'books';
      case 'Self-Learning Hub':
        return currentPage === 'videoLearning';
      case 'Blogs':
        return currentPage === 'blogs';
      case 'My Dashboard':
        return currentPage === 'profile' as any;
      case 'Admin':
        return currentPage === 'admin';
      case 'MK Academy':
        return currentPage === 'onlineClasses' || currentPage === 'inPersonClasses';
      default:
        return false;
    }
  };

  // Debug: Log the MK Academy functions
  console.log('🔍 Header MK Academy functions:', {
    onMkAcademyOnlineClick: onMkAcademyOnlineClick,
    onMkAcademyInPersonClick: onMkAcademyInPersonClick,
    currentPage: currentPage
  });

  const navItems: NavItem[] = [
    { name: 'Home', href: '#home' },
    { name: 'Books', href: '#books' },
    { name: 'Self-Learning Hub', href: '#video-learning' },
    { 
      name: 'MK Academy', 
      href: '#mk-academy',
      dropdown: [
        { 
          name: 'Online Classes', 
          href: '#online-classes',
          action: onMkAcademyOnlineClick 
        },
        { 
          name: 'InPerson Classes', 
          href: '#inperson-classes',
          action: onMkAcademyInPersonClick 
        }
      ]
    },
    { name: 'College Prep', href: 'https://collegeprep.math-knots.com', external: true },
    { name: 'Blogs', href: '#blogs' },
    // Only show Dashboard link if user is authenticated
    ...(isAuthenticated ? [{ name: 'My Dashboard', href: '#dashboard' }] : []),
    // Only show Admin link if user is authenticated AND is admin
    ...(isAuthenticated && isAdmin ? [{ name: 'Admin', href: '#admin' }] : []),
  ];

  return (
    <HeaderContainer>
      <TopBar>
        <TopBarContent>
          <TopContactInfo>
            <span className="contact-label">📧 Contact:</span>
            <a href="mailto:mathknots.help@gmail.com" className="email">
              mathknots.help@gmail.com
            </a>
          </TopContactInfo>
          
          {isAuthenticated && user ? (
            <UserInfo>
              <UserName 
                onClick={() => window.location.href = '/?page=profile'}
                style={{ cursor: 'pointer' }}
                title="View Profile"
              >
                Welcome, {user.name}
              </UserName>
              <span>({user.role})</span>
              <ProfileLink onClick={() => window.location.href = '/?page=profile'}>
                My Achievements
              </ProfileLink>
              {onLogout && (
                <LogoutButton onClick={onLogout}>
                  <FiLogOut size={14} />
                  Logout
                </LogoutButton>
              )}
            </UserInfo>
          ) : (
            <UserInfo>
              {/* Removed duplicate login button - only use the one in RightSection */}
            </UserInfo>
          )}
        </TopBarContent>
      </TopBar>
      
      <HeaderContent>
        <LogoSection>
          <Logo onClick={onHomeClick}>
            <LogoImage src="/mathknots-logo.png" alt="MathKnots" />
          </Logo>
        </LogoSection>

        <NavMenu>
          {navItems.map((item) => (
            <NavItem key={item.name} $isActive={isNavItemActive(item.name)}>
              <div 
                className="nav-link"
                onClick={() => {
                  if (item.external && item.href) {
                    window.open(item.href, '_blank', 'noopener,noreferrer');
                  }
                  else if (item.name === 'Home') onHomeClick();
                  else if (item.name === 'Books') onBooksClick();
                  else if (item.name === 'Self-Learning Hub') onVideoLearningClick();
                  else if (item.name === 'Blogs') onBlogsClick();
                  else if (item.name === 'My Dashboard') {
                    if (onDashboardClick) onDashboardClick();
                    else window.location.href = '/?page=profile';
                  }
                  else if (item.name === 'Admin') onAdminClick();
                }}
              >
                {item.name === 'Home' && <FiHome />}
                {item.name === 'Books' && <FiBook />}
                {item.name === 'Self-Learning Hub' && <FiVideo />}
                {item.name === 'MK Academy' && <FiBookOpen />}
                {item.name === 'College Prep' && <FiAward />}
                {item.name === 'Blogs' && <FiMessageSquare />}
                {item.name === 'My Dashboard' && <FiGrid />}
                {item.name === 'Admin' && <FiUser />}

                
                <span>{item.name}</span>
                
                {item.dropdown && (
                  <FiChevronDown size={14} style={{ marginLeft: '4px' }} />
                )}
              </div>
              {item.dropdown && (
                <DropdownMenu>
                  {item.dropdown.map((dropdownItem) => (
                    <DropdownItem key={dropdownItem.name}>
                      {dropdownItem.action ? (
                        <div 
                          onClick={() => {
                            console.log('🔍 Dropdown item clicked:', dropdownItem.name);
                            console.log('🔍 Action function:', dropdownItem.action);
                            if (dropdownItem.action) {
                              dropdownItem.action();
                            } else {
                              console.error('❌ No action function defined for:', dropdownItem.name);
                            }
                          }}
                          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                        >
                          {dropdownItem.name === 'Online Classes' && <FiMonitor size={16} />}
                          {dropdownItem.name === 'InPerson Classes' && <FiCalendar size={16} />}
                          {dropdownItem.name}
                        </div>
                      ) : (
                        <a href={dropdownItem.href}>{dropdownItem.name}</a>
                      )}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              )}
            </NavItem>
          ))}
        </NavMenu>

        <RightSection>
          {/* Only show this login button if user is NOT authenticated */}
          {!isAuthenticated && onLoginClick && (
            <AuthButton onClick={onLoginClick}>Login</AuthButton>
          )}
          <IconButton onClick={() => {
            console.log('Cart icon clicked, opening cart');
            console.log('Current cart count:', cartCount);
            setIsCartOpen(true);
          }} style={{ position: 'relative' }}>
            <FiShoppingCart />
            {cartCount > 0 && (
              <CartBadge>{cartCount}</CartBadge>
            )}
          </IconButton>
          <MobileMenuButton onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            {isMobileMenuOpen ? <FiX /> : <FiMenu />}
          </MobileMenuButton>
        </RightSection>
      </HeaderContent>

      {isMobileMenuOpen && (
        <MobileMenu>
          {navItems.map((item) => (
            <React.Fragment key={item.name}>
              <MobileNavItem $isActive={isNavItemActive(item.name)}>
                <div 
                  onClick={() => {
                    if (item.external && item.href) {
                      window.open(item.href, '_blank', 'noopener,noreferrer');
                    }
                    else if (item.name === 'Home') onHomeClick();
                    else if (item.name === 'Books') onBooksClick();
                    else if (item.name === 'Self-Learning Hub') onVideoLearningClick();
                    else if (item.name === 'Blogs') onBlogsClick();
                    else if (item.name === 'My Dashboard') {
                      if (onDashboardClick) onDashboardClick();
                      else window.location.href = '/?page=profile';
                    }
                    else if (item.name === 'Admin') onAdminClick();
                    if (!item.dropdown) setIsMobileMenuOpen(false);
                  }}
                  style={{ 
                    fontWeight: item.dropdown ? '600' : '400',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {item.name === 'Home' && <FiHome />}
                  {item.name === 'Books' && <FiBook />}
                  {item.name === 'Self-Learning Hub' && <FiVideo />}
                  {item.name === 'MK Academy' && <FiBookOpen />}
                  {item.name === 'College Prep' && <FiAward />}
                  {item.name === 'Blogs' && <FiMessageSquare />}
                  {item.name === 'My Dashboard' && <FiGrid />}
                  {item.name === 'Admin' && <FiUser />}
                  {item.name}
                </div>
              </MobileNavItem>
              
              {/* Render dropdown items for mobile */}
              {item.dropdown && item.dropdown.map((dropdownItem) => (
                <MobileNavItem 
                  key={dropdownItem.name} 
                  $isSubItem={true}
                  $isActive={
                    (dropdownItem.name === 'Online Classes' && currentPage === 'onlineClasses') ||
                    (dropdownItem.name === 'InPerson Classes' && currentPage === 'inPersonClasses')
                  }
                >
                  <div 
                    onClick={() => {
                      if (dropdownItem.action) {
                        dropdownItem.action();
                      }
                      setIsMobileMenuOpen(false);
                    }}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem' }}
                  >
                    {dropdownItem.name === 'Online Classes' && <FiMonitor size={16} />}
                    {dropdownItem.name === 'InPerson Classes' && <FiCalendar size={16} />}
                    {dropdownItem.name}
                  </div>
                </MobileNavItem>
              ))}
            </React.Fragment>
          ))}
        </MobileMenu>
      )}

      {/* Cart Display Modal */}
      <CartDisplay 
        isOpen={isCartOpen} 
        onClose={() => setIsCartOpen(false)}
        onContinueShopping={onHomeClick}
        isAuthenticated={isAuthenticated}
        onLoginClick={onLoginClick}
      />

      {/* Chatbot Component */}
      <Chatbot 
        isOpen={isChatbotOpen} 
        onToggle={() => setIsChatbotOpen(!isChatbotOpen)}
        onBooksClick={onBooksClick}
        onVideoLearningClick={onVideoLearningClick}
        onBlogsClick={onBlogsClick}
        onMkAcademyOnlineClick={onMkAcademyOnlineClick}
        onMkAcademyInPersonClick={onMkAcademyInPersonClick}
      />
    </HeaderContainer>
  );
};

export default Header; 